<template>
  <div
    class="relative w-full overflow-hidden px-4 pt-[var(--status-bar-height)]"
  >
    <div
      class="absolute left-0 top-0 h-[calc(var(--status-bar-height,0px)+190px)] w-full"
    >
      <picture>
        <source :srcset="getVisualSrc('webp')" type="image/webp" />
        <source :srcset="getVisualSrc()" type="image/png" />
        <img :src="getVisualSrc()" alt="cover" class="object-cover" />
      </picture>
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-0 top-0 h-[calc(var(--status-bar-height,0px)+72px)] bg-gradient-to-b from-black/35 via-black/15 to-transparent"
      />
      <div class="absolute inset-0 bg-[#171E1A]/30" />
      <div class="thumb" :style="{ opacity: thumbOpacity }" />
    </div>
    <FreeHeader v-if="showBurgerMenu" class="h-[56px]">
      <template #leftAction>
        <button
          class="text-static-white relative"
          type="button"
          @click="handleMenuClick"
        >
          <span
            v-if="showMenuBadge"
            class="bg-background-negative-primary absolute right-[2.5px] top-[2.5px] block size-1.5 rounded-full"
          />
          <HamburgerIcon />
        </button>
      </template>
      <template #default>
        <h2 class="text-static-white text-center text-lg font-semibold">
          {{ t('mainPageHeader.travel') }}
        </h2>
      </template>
      <template #rightAction>
        <button
          type="button"
          class="block size-7 backdrop-blur-[21.75px] [clip-path:inset(0_round_14px)]"
          @click="handleCrossButtonClick"
        >
          <CloseBlurIcon />
        </button>
      </template>
    </FreeHeader>
    <header
      v-else
      class="relative flex h-[56px] w-full items-center justify-between"
    >
      <h2 class="text-static-white text-lg font-semibold">
        {{ t('mainPageHeader.travel') }}
      </h2>
      <div class="flex items-center gap-2">
        <slot name="rightExtra" />
        <button
          type="button"
          class="block size-7 backdrop-blur-[21.75px] [clip-path:inset(0_round_14px)]"
          @click="handleCrossButtonClick"
        >
          <CloseBlurIcon />
        </button>
      </div>
    </header>
    <div class="-mx-4 overflow-hidden rounded-t-3xl">
      <div
        ref="scrollableRef"
        class="h-[calc(100vh-56px-var(--status-bar-height,0px))] snap-y snap-mandatory overflow-y-auto overscroll-none"
      >
        <div class="sticky top-0 snap-start">
          <AppSwitchButton
            :active-app="searchAppType"
            source-page="main"
            class="py-4"
            :style="{
              opacity: appSwitchOpacity,
              pointerEvents: collapseProgress > 0.5 ? 'none' : 'auto',
            }"
            @onMiniAppSwitch="emit('onMiniAppSwitch', $event)"
          />
        </div>
        <div class="snap-start">
          <slot />
        </div>
      </div>
    </div>
    <ProfileLinksBottomSheet
      :search-app-type="searchAppType"
      @onStartLoading="emit('onStartLoading')"
      @onMyTicketsClick="emit('onMyTicketsClick')"
    >
      <template #[FlowType.TOURS]>
        <slot :name="FlowType.TOURS" />
      </template>
    </ProfileLinksBottomSheet>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { controllerModel } from 'shared';

  import HamburgerIcon from '../../../assets/icons/28/hamburger.svg?component';
  import CloseBlurIcon from '../../../assets/icons/28/close-blur.svg?component';
  import { AppSwitchButton } from '../../../features/app-switch-button';
  import { freedomApp } from '../../../entities/freedom-app';
  import { FlowType } from '../../../shared/config';
  import { FreeHeader } from '../../../shared/ui';
  import { PROFILE_LINKS_BOTTOM_SHEET_NAME } from '../config';
  import ProfileLinksBottomSheet from './ProfileLinksBottomSheet.vue';

  const { searchAppType = FlowType.AVIA, showMenuBadge = false } = defineProps<{
    searchAppType?: FlowType;
    showMenuBadge?: boolean;
    showBurgerMenu?: boolean;
  }>();

  const emit = defineEmits<{
    onStartLoading: [];
    onMyTicketsClick: [];
    onMenuOpen: [];
    onMiniAppSwitch: [app: FlowType];
  }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();

  const scrollableRef = useTemplateRef<HTMLElement>('scrollableRef');
  const collapseProgress = ref(0);

  const appSwitchOpacity = computed(() =>
    Math.max(0, 1 - collapseProgress.value * 2.5).toFixed(3),
  );

  const thumbOpacity = computed(() =>
    Math.max(0, 1 - collapseProgress.value).toFixed(3),
  );

  const handleMenuClick = () => {
    emit('onMenuOpen');
    bottomSheet.show(PROFILE_LINKS_BOTTOM_SHEET_NAME);
  };

  const handleCrossButtonClick = () => {
    freedomApp.lib.backToApp();
  };

  const handleScroll = () => {
    const el = scrollableRef.value;
    if (!el) {
      return;
    }

    collapseProgress.value = Math.min(Math.max(el.scrollTop / 160, 0), 1);
  };

  watch(scrollableRef, (el, _, onCleanup) => {
    if (!el) {
      return;
    }

    el.addEventListener('scroll', handleScroll, { passive: true });
    onCleanup(() => {
      el.removeEventListener('scroll', handleScroll);
    });
  });

  const getVisualSrc = (format: 'webp' | 'png' = 'png') => {
    switch (searchAppType) {
      case FlowType.AVIA:
        return new URL(`./assets/avia-visual.${format}`, import.meta.url).href;
      case FlowType.RAILWAYS:
        return new URL(`./assets/rw-visual.${format}`, import.meta.url).href;
      case FlowType.TOURS:
        return new URL(`./assets/tours-visual.${format}`, import.meta.url).href;
      case FlowType.HOTELS:
        return new URL(`./assets/hotels-visual.${format}`, import.meta.url)
          .href;
    }
  };

  defineExpose({
    scrollableRef,
  });
</script>

<style scoped>
  .thumb {
    @apply absolute bottom-0 left-0 h-[calc(92px+var(--status-bar-height))] w-full;
    backdrop-filter: blur(20px);
    background: rgb(var(--static-black) / 0.4);
    -webkit-mask-image: linear-gradient(
      180deg,
      rgba(217, 217, 217, 0) 0%,
      rgba(217, 217, 217, 0.815457) 38.17%,
      rgba(217, 217, 217, 0.943687) 50.5%,
      #d9d9d9 63.45%
    );
    mask-image: linear-gradient(
      180deg,
      rgba(217, 217, 217, 0) 0%,
      rgba(217, 217, 217, 0.815457) 38.17%,
      rgba(217, 217, 217, 0.943687) 50.5%,
      #d9d9d9 63.45%
    );
  }
</style>

<i18n src="../config/locales.json"></i18n>
